'use client';

import { yupResolver } from '@hookform/resolvers/yup';
import { ComponentProps, ReactNode } from 'react';
import {
    DefaultValues,
    FieldValues,
    FormProvider,
    SubmitHandler,
    useForm,
} from 'react-hook-form';

import { yup } from '@/lib/validation/yup';
import { cx } from '@/ui/cva';

type FormProps<TFieldValues extends FieldValues> = Omit<
    ComponentProps<'form'>,
    'action' | 'method' | 'onSubmit' | 'noValidate'
> & {
    children: ReactNode;
    initialValues?: DefaultValues<
        yup.InferType<yup.ObjectSchema<TFieldValues>>
    >;
    schema: yup.ObjectSchema<TFieldValues>;
    onSubmit: SubmitHandler<yup.InferType<yup.ObjectSchema<TFieldValues>>>;
};

export function Form<TFieldValues extends FieldValues>({
    children,
    className,
    initialValues,
    schema,
    onSubmit,
    ...props
}: FormProps<TFieldValues>) {
    const form = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(schema),
    });

    return (
        <FormProvider {...form}>
            <form
                {...props}
                className={cx('flex flex-col items-stretch gap-md', className)}
                noValidate
                onSubmit={form.handleSubmit(onSubmit)}
            >
                {children}
            </form>
        </FormProvider>
    );
}
